import axios from 'axios';

const BACKEND_BASE_URL = 'https://weup1lmf01func01.azurewebsites.net/api';
const API_BASE_URL = 'https://api.themoviedb.org/3';
const API_KEY = process.env.REACT_APP_TMDB_API_KEY || '422d83b423e9fbf377333625a314dfea';
const FUNCTION_KEY_MOVIE = process.env.REACT_APP_LMF_MOVIE_FUNCTION_KEY;
const FUNCTION_KEY_TV = process.env.REACT_APP_LMF_TV_FUNCTION_KEY;

// Create axios instances with timeout
export const instance = axios.create({
  baseURL: BACKEND_BASE_URL,
  timeout: 5000,
});

export const instanceapi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 8000,
});

export const searchMovieOrTVShow = async (query) => {
  try {
    const response = await instanceapi.get('/search/multi', {
      params: {
        api_key: API_KEY,
        query: query,
        page: 1,
      },
    });
    return response.data.results || [];
  } catch (error) {
    console.error('Error in searchMovieOrTVShow:', error);
    return [];
  }
};

export const getIMDbId = async (id, mediaType) => {
  try {
    const response = await instanceapi.get(`/${mediaType}/${id}/external_ids`, {
      params: {
        api_key: API_KEY,
      },
    });
    return response.data.imdb_id || null;
  } catch (error) {
    return null;
  }
};

export const getStreamingInfo = async (id, mediaType) => {
  try {
    const response = await instanceapi.get(`/${mediaType}/${id}/watch/providers`, {
      params: {
        api_key: API_KEY,
      },
    });
    const providers = response.data?.results?.NL;
    if (providers) {
      return {
        buy: providers.buy || [],
        flatrate: providers.flatrate || [],
        rent: providers.rent || [],
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const getTrailer = async (id, mediaType) => {
  try {
    const response = await instanceapi.get(`/${mediaType}/${id}/videos`, {
      params: {
        api_key: API_KEY,
        language: 'en-US',
      },
    });

    let videos = response.data?.results;
    if (!videos || videos.length === 0) {
      const fallbackResponse = await instanceapi.get(`/${mediaType}/${id}/videos`, {
        params: {
          api_key: API_KEY,
        },
      });
      videos = fallbackResponse.data?.results;
    }

    if (!videos || videos.length === 0) return null;

    const priorityOrder = ['Trailer', 'Teaser', 'Clip', 'Featurette'];
    const sortedVideos = [...videos].sort((a, b) => {
      const typeA = priorityOrder.indexOf(a.type);
      const typeB = priorityOrder.indexOf(b.type);
      if (typeA !== typeB) return (typeA === -1 ? 99 : typeA) - (typeB === -1 ? 99 : typeB);
      const officialA = a.name.toLowerCase().includes('official') ? 0 : 1;
      const officialB = b.name.toLowerCase().includes('official') ? 0 : 1;
      return officialA - officialB;
    });

    const bestVideo = sortedVideos.find((v) => v.site === 'YouTube');
    return bestVideo || null;
  } catch (error) {
    return null;
  }
};

// Robust getPopularMovies with automatic TMDB direct fallback
export const getPopularMovies = async () => {
  // 1. Try Azure Backend first
  if (FUNCTION_KEY_MOVIE) {
    try {
      const response = await instance.get('/GetPopularMovies', {
        params: { code: FUNCTION_KEY_MOVIE },
      });
      const data = response.data;
      if (data && typeof data === 'object' && Array.isArray(data.movies) && data.movies.length > 0) {
        return data.movies;
      }
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }
    } catch (backendError) {
      console.warn('Backend GetPopularMovies unavailable, using TMDB fallback:', backendError.message);
    }
  }

  // 2. Direct TMDB fallback
  try {
    const pages = [1, 2, 3, 4];
    const responses = await Promise.all(
      pages.map((page) =>
        instanceapi.get('/movie/popular', {
          params: {
            api_key: API_KEY,
            page: page,
            language: 'en-US',
            region: 'NL',
          },
        })
      )
    );

    const allMovies = [];
    responses.forEach((res) => {
      if (res?.data?.results) {
        const qualityItems = res.data.results.filter(
          (item) => item.poster_path && item.vote_count > 50
        );
        allMovies.push(...qualityItems);
      }
    });
    return allMovies;
  } catch (error) {
    console.error('Direct TMDB popular movies error:', error);
    return [];
  }
};

// Robust getPopularTVShows with automatic TMDB direct fallback
export const getPopularTVShows = async () => {
  // 1. Try Azure Backend first
  if (FUNCTION_KEY_TV) {
    try {
      const response = await instance.get('/GetPopularTVShows', {
        params: { code: FUNCTION_KEY_TV },
      });
      const data = response.data;
      if (data && typeof data === 'object' && Array.isArray(data.tvShows) && data.tvShows.length > 0) {
        return data.tvShows;
      }
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }
    } catch (backendError) {
      console.warn('Backend GetPopularTVShows unavailable, using TMDB fallback:', backendError.message);
    }
  }

  // 2. Direct TMDB fallback
  try {
    const pages = [1, 2, 3, 4];
    const responses = await Promise.all(
      pages.map((page) =>
        instanceapi.get('/tv/popular', {
          params: {
            api_key: API_KEY,
            page: page,
            language: 'en-US',
            region: 'NL',
          },
        })
      )
    );

    const allTV = [];
    responses.forEach((res) => {
      if (res?.data?.results) {
        const qualityItems = res.data.results.filter(
          (item) => item.poster_path && item.vote_count > 30
        );
        allTV.push(...qualityItems);
      }
    });
    return allTV;
  } catch (error) {
    console.error('Direct TMDB popular TV shows error:', error);
    return [];
  }
};

// Batch metadata hydrator
export const getDetailedResultsBatch = async (items, mediaType, batchSize = 3) => {
  if (!items || items.length === 0) return [];

  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);

    const batchPromises = batch.map(async (item) => {
      try {
        const currentMediaType = mediaType || item.media_type || (item.title ? 'movie' : 'tv');

        const [streamingInfo, trailer, imdbId] = await Promise.all([
          getStreamingInfo(item.id, currentMediaType).catch(() => null),
          getTrailer(item.id, currentMediaType).catch(() => null),
          getIMDbId(item.id, currentMediaType).catch(() => null),
        ]);

        const hasFlatrate = streamingInfo && streamingInfo.flatrate && streamingInfo.flatrate.length > 0;
        const flatrateProviders = hasFlatrate ? streamingInfo.flatrate : [];

        return {
          ...item,
          mediaType: currentMediaType,
          imdbId,
          imdbURL: imdbId ? `https://www.imdb.com/title/${imdbId}` : null,
          streamingProviders: flatrateProviders,
          hasStreaming: flatrateProviders.length > 0,
          trailer: trailer ? trailer.key : null,
          tmdbURL: `https://www.themoviedb.org/${currentMediaType}/${item.id}`,
        };
      } catch (error) {
        return {
          ...item,
          mediaType: mediaType || item.media_type || (item.title ? 'movie' : 'tv'),
          streamingProviders: [],
          trailer: null,
          tmdbURL: `https://www.themoviedb.org/${mediaType || 'movie'}/${item.id}`,
        };
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.filter(Boolean));
    if (results.length >= 6) break;
  }

  return results;
};